import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ABC_FIRMWARE } from '../upload/fetchFirmware'
import { type UploadFile } from '../upload/uploadAudio'
import { useToyMaker } from '../upload/useToyMaker'
import type { UploadState } from '../upload/uploadState'
import { groupByFirstLetter, parseWords } from './makeAbc.helpers'
import { designApiKey } from '../lib/elevenLabsKey'
import './MakeMusicPlayer.css'

const ELEVENLABS_VOICE_ID = 'ZF6FPAbjXT4488VcRRnw'
const ELEVENLABS_MODEL_ID = 'eleven_v3'

const LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))
const EXTRA_BUNDLED_FILES = ['now_i.mp3', 'nexttime.mp3']

// ABC has an extra `'tts'` phase that lives between the user's "Make it"
// click and the call into the shared packager. It's not in the base
// UploadState union; we re-type the hook's setUpload to accept it.
type TtsUploadState =
  | UploadState
  | { status: 'tts'; done: number; total: number; current: string }

async function fetchLetterMp3s(): Promise<UploadFile[]> {
  const names = [...LETTERS.map((l) => `${l}.mp3`), ...EXTRA_BUNDLED_FILES]
  return Promise.all(
    names.map(async (name) => {
      const res = await fetch(`/abc/${name}`)
      if (!res.ok) {
        throw new Error(`Failed to fetch /abc/${name}: ${res.status}`)
      }
      return { name, blob: await res.blob() }
    }),
  )
}

async function ttsToMp3(apiKey: string, text: string): Promise<Blob> {
  const url = `https://api.elevenlabs.io/v1/text-to-speech/${ELEVENLABS_VOICE_ID}?output_format=mp3_44100_64`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'xi-api-key': apiKey,
      'Content-Type': 'application/json',
      Accept: 'audio/mpeg',
    },
    body: JSON.stringify({ text, model_id: ELEVENLABS_MODEL_ID }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`ElevenLabs ${res.status}: ${body.slice(0, 200) || res.statusText}`)
  }
  return res.blob()
}

export default function MakeABC() {
  const [text, setText] = useState('')

  const apiKey = designApiKey((import.meta.env.VITE_ELEVENLABS_API_KEY ?? '').trim())

  const words = parseWords(text)
  const groups = groupByFirstLetter(words)
  const skipped = words.length - [...groups.values()].reduce((n, arr) => n + arr.length, 0)

  const toyMaker = useToyMaker({
    makerApp: 'abc',
    hasPayload: groups.size > 0 && !!apiKey,
    firmwareTarget: ABC_FIRMWARE,
    appNameFallback: 'Oroblanco ABC',
    buildPayload: async () => {
      if (!apiKey) {
        throw new Error('VITE_ELEVENLABS_API_KEY is not set in .env.local.')
      }
      const flatWords = [...groups.values()].flat()
      const setUploadExt = toyMaker.setUpload as unknown as (s: TtsUploadState) => void
      // TTS each word into an A/1.mp3-style UploadFile. Done inline because
      // this is the part unique to ABC — the shared packager takes over
      // once we have a complete payloadFiles list.
      const ttsFiles: UploadFile[] = []
      let done = 0
      const total = flatWords.length
      for (const [letter, letterWords] of groups) {
        for (let i = 0; i < letterWords.length; i++) {
          const word = letterWords[i]
          setUploadExt({
            status: 'tts',
            done,
            total,
            current: `${letter}: ${word}`,
          })
          const blob = await ttsToMp3(apiKey, word)
          ttsFiles.push({ name: `${letter}/${i + 1}.mp3`, blob })
          done++
        }
      }
      const letterFiles = await fetchLetterMp3s()
      const payloadFiles: UploadFile[] = [...letterFiles, ...ttsFiles]
      return { payloadFiles, extraMetadata: { words: flatWords } }
    },
  })

  const upload = toyMaker.upload as TtsUploadState
  const ttsState = upload.status === 'tts' ? upload : null
  const isWorking = toyMaker.isWorking || ttsState !== null

  return (
    <section className="music-player">
      <Link to="/" className="detail-back">&larr; Back to Toys</Link>
      <h1 className="page-title">ABC</h1>
      <p className="page-lede">
        Enter comma-separated English words or phrases. We&rsquo;ll generate a voice recording for
        each one and group them on your toy by their first letter. For example,{' '}
        <em>apple, banana, ant, dog, frog, elephant</em>{' '}...
        Words that don&rsquo;t start with a letter A–Z are skipped. For best results include at least
        a few words for each letter.
      </p>

      {!apiKey && (
        <p className="music-player__status music-player__status--err">
          VITE_ELEVENLABS_API_KEY is not set in .env.local.
        </p>
      )}

      <div className="card music-player__panel">
        <textarea
          className="make-abc__textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="apple, ant, arrow, banana, ball, …"
          rows={10}
          disabled={isWorking}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            fontSize: '1rem',
            padding: '0.75rem',
          }}
        />
      </div>

      <div className="music-player__actions">
        <span className="music-player__count">
          {words.length === 0
            ? '0 words'
            : `${words.length} word${words.length === 1 ? '' : 's'} across ${groups.size} letter${
                groups.size === 1 ? '' : 's'
              }${skipped > 0 ? ` (${skipped} skipped)` : ''}`}
        </span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!toyMaker.canSubmit}
          onClick={toyMaker.onDoneClick}
        >
          {isWorking ? 'Working…' : 'Done, make my toy'}
        </button>
      </div>

      {ttsState && (
        <p className="music-player__status music-player__status--info">
          Generating audio — {ttsState.done} of {ttsState.total} — {ttsState.current}
        </p>
      )}

      {toyMaker.modals}
    </section>
  )
}
